<template>
    <v-app id="inspire" style="background-color: #f4f6f9;">
        <v-main>
            <v-container fluid fill-height>
                <v-layout align-center justify-center>
                    <v-flex xs12 sm8 md6 lg4>
                        <v-card class="elevation-2 pa-8 rounded-lg login-card" dir="rtl">
                            <v-card-text class="text-center pa-0">
                                <!-- The organisation (Management ▸ الإعدادات ▸ بيانات المؤسسة); each line only when set. -->
                                <div v-if="hasIdentity" class="mb-6">
                                    <img
                                        v-if="showLogo"
                                        :src="logoUrl"
                                        :alt="`شعار ${displayName}`"
                                        class="org-logo"
                                        :class="{ 'mb-4': org.parentNameAr || org.nameAr || org.nameEn }"
                                        @error="logoFailed = true"
                                    />
                                    <div v-if="org.parentNameAr" class="text-body-2 grey--text text--darken-1">
                                        {{ org.parentNameAr }}
                                    </div>
                                    <div v-if="org.nameAr" class="text-h6 font-weight-bold black--text">
                                        {{ org.nameAr }}
                                    </div>
                                    <div v-if="org.nameEn" class="text-caption grey--text text--darken-1" dir="ltr">
                                        {{ org.nameEn }}
                                    </div>
                                </div>

                                <v-divider v-if="hasIdentity" class="mb-6"></v-divider>

                                <h3 class="text-subtitle-1 font-weight-bold black--text mb-6">
                                    تسجيل الدخول إلى {{ displayName }}
                                </h3>

                                <v-alert
                                    v-if="formError"
                                    type="warning"
                                    dense
                                    text
                                    class="text-right mb-4"
                                >
                                    {{ formError }}
                                </v-alert>

                                <v-form @submit.prevent="submit" class="text-right">
                                    <div class="font-weight-bold mb-2 black--text">اسم المستخدم:</div>
                                    <v-text-field
                                        v-model.trim="user.loginName"
                                        placeholder="أدخل اسم المستخدم"
                                        autocomplete="username"
                                        outlined
                                        dense
                                        class="mb-2"
                                    ></v-text-field>

                                    <div class="font-weight-bold mb-2 black--text">كلمة المرور:</div>
                                    <v-text-field
                                        v-model="user.password"
                                        :append-icon="isPasswordVisible ? 'mdi-eye' : 'mdi-eye-off'"
                                        :type="isPasswordVisible ? 'text' : 'password'"
                                        @click:append="isPasswordVisible = !isPasswordVisible"
                                        placeholder="••••••••"
                                        autocomplete="current-password"
                                        outlined
                                        dense
                                        class="mb-2"
                                    ></v-text-field>

                                    <div class="font-weight-bold mb-2 black--text">رمز التحقق:</div>
                                    <div class="captcha-row mb-3">
                                        <div class="captcha-image" aria-live="polite">
                                            <v-progress-circular
                                                v-if="captchaLoading"
                                                indeterminate
                                                color="#16375A"
                                                size="30"
                                            ></v-progress-circular>
                                            <img
                                                v-else-if="captchaImage"
                                                :src="captchaImage"
                                                alt="صورة رمز التحقق"
                                            />
                                            <span v-else class="grey--text text-caption">تعذر تحميل الرمز</span>
                                        </div>

                                        <v-btn
                                            type="button"
                                            icon
                                            color="#16375A"
                                            :loading="captchaLoading"
                                            :disabled="captchaLoading"
                                            aria-label="تحديث رمز التحقق"
                                            title="تحديث رمز التحقق"
                                            @click="loadCaptcha"
                                        >
                                            <v-icon>mdi-refresh</v-icon>
                                        </v-btn>
                                    </div>

                                    <v-text-field
                                        v-model.trim="user.captchaText"
                                        placeholder="اكتب الأحرف الظاهرة في الصورة"
                                        autocomplete="off"
                                        maxlength="6"
                                        outlined
                                        dense
                                        class="captcha-input"
                                    ></v-text-field>

                                    <v-btn
                                        type="submit"
                                        color="#16375A"
                                        block
                                        large
                                        class="mt-4 font-weight-bold"
                                        style="border-radius: 8px; color: #D4A361 !important;"
                                        :disabled="captchaLoading || !user.captchaId"
                                    >
                                        تسجيل الدخول
                                    </v-btn>
                                </v-form>
                            </v-card-text>
                        </v-card>
                    </v-flex>
                </v-layout>
            </v-container>
        </v-main>
    </v-app>
</template>

<script>
export default {
    data() {
        return {
            isPasswordVisible: false,
            logoFailed: false,
            captchaLoading: false,
            captchaImage: null,
            formError: null,
            user: {
                loginName: "",
                password: "",
                captchaId: "",
                captchaText: "",
            },
        };
    },
    computed: {
        // The organisation identity, loaded by App.vue before sign-in.
        org() {
            return this.$store.state.organization.org;
        },
        displayName() {
            return this.$store.getters["organization/displayName"];
        },
        logoUrl() {
            return this.$store.getters["organization/logoUrl"];
        },
        showLogo() {
            return !!this.logoUrl && !this.logoFailed;
        },
        hasIdentity() {
            return this.showLogo || !!(this.org.parentNameAr || this.org.nameAr || this.org.nameEn);
        },
    },
    watch: {
        // A new logo URL (the identity loaded, or a new upload) gets a fresh chance to load.
        logoUrl() {
            this.logoFailed = false;
        },
    },
    mounted() {
        this.loadCaptcha();
    },
    methods: {
        async loadCaptcha() {
            this.captchaLoading = true;
            this.user.captchaId = "";
            this.user.captchaText = "";

            try {
                const response = await this.$store.dispatch("security/getCaptcha");
                this.captchaImage = response.imageData;
                this.user.captchaId = response.captchaId;
            } catch (error) {
                this.captchaImage = null;
                this.formError = "تعذر تحميل رمز التحقق. اضغط على زر التحديث وحاول مرة أخرى.";
            } finally {
                this.captchaLoading = false;
            }
        },
        async submit() {
            this.formError = null;

            if (!this.user.loginName || !this.user.password || !this.user.captchaText) {
                this.formError = "الرجاء إدخال اسم المستخدم وكلمة المرور ورمز التحقق.";
                return;
            }

            try {
                const response = await this.$store.dispatch("security/login", this.user);
                if (response.authenticatedUser.requiresPasswordChange) {
                    this.$router.push({ name: "ChangePassword" });
                } else {
                    this.$router.push({ name: "Layout" });
                }
            } catch (error) {
                await this.loadCaptcha();
            }
        },
    },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');

#inspire {
    font-family: 'Cairo', sans-serif;
}

.login-card {
    width: 100%;
}

.org-logo {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    max-height: 140px;
    object-fit: contain;
}

.v-text-field--outlined fieldset {
    border-color: #e0e0e0;
}

.captcha-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.captcha-image {
    min-height: 84px;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #d8dde3;
    border-radius: 8px;
    background: #ffffff;
}

.captcha-image img {
    display: block;
    width: 100%;
    height: 84px;
    object-fit: contain;
    user-select: none;
}

.captcha-input ::v-deep input {
    direction: ltr;
    text-align: center;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 700;
}
</style>
